import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import type { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium';

import { gql } from '@graphql/__generated__';
import {
  type GetListingsMatchingQuery,
  type Lots_Bool_Exp,
  type Lots_Order_By,
  Order_By,
} from '@graphql/__generated__/graphql';
import { ListingCell } from '@modules/_shared/components/data-grid/ListingCell';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import type { QuickFilterDefinition } from '@modules/_shared/components/filters/Filters';
import { getValueFromPath } from '@modules/_shared/components/filters/SchemaProvider';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { getLotStatusTagVariant } from '@modules/_shared/components/LotCardInfo';
import { MutationErrorAlert } from '@modules/_shared/components/MutationErrorModal';
import { Tag } from '@modules/_shared/components/Tag';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import {
  parseOrderByParam,
  withTiebreaker,
} from '@modules/_shared/utils/listUrlParams';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { getListingPrices } from '@modules/_shared/utils/propertyTemplateFunctions';
import { GET_LOTS_LIST_COUNT } from '@modules/listings/lotsQueries';
import {
  type IntlLocale,
  type Translate,
  useLocale,
} from '@shared/hooks/locale';

type UserBuyerLeadMatchingListProps = {
  excludedListings: string[];
  onAdd: (ids: string[]) => void;
  isAdding?: boolean;
  onCancel: () => void;
};

const useListingsMatchingParams = (defaultFilters?: Lots_Bool_Exp) => {
  const [searchParams] = useScopedSearchParams('matching');

  return useMemo(() => {
    const where: Lots_Bool_Exp = JSON.parse(searchParams.get('where') || '{}');
    const order_by = withTiebreaker<Lots_Order_By>(
      parseOrderByParam<Lots_Order_By>(searchParams.get('order_by'), [
        { created_at: Order_By.Desc },
      ]),
      { id: Order_By.Asc },
      'id',
    );

    const { limit, offset, countLimit } = getPaginationVariables(
      searchParams,
      undefined,
      true,
    );

    if (defaultFilters != null) {
      where._and = [
        ...(where._and ?? []),
        ...Object.entries(defaultFilters).reduce((acc, [key, value]) => {
          acc.push({ [key]: value });

          return acc;
        }, [] as Lots_Bool_Exp[]),
      ];
    }

    return {
      where,
      order_by,
      limit,
      offset,
      countLimit,
    };
  }, [searchParams, defaultFilters]);
};

const GET_LISTINGS_MATCHING = gql(/* GraphQL */ `
  query GetListingsMatching(
    $where: lots_bool_exp!
    $order_by: [lots_order_by!]!
    $limit: Int!
    $offset: Int!
  ) {
    lots(where: $where, order_by: $order_by, limit: $limit, offset: $offset) {
      id
      status
      lots_status {
        label
      }
      ...ListingCell_listing
      ...ListingPrice
      broker {
        id
        ...UserInfo_user
      }
      last_enquiry: enquiries_aggregate {
        aggregate {
          max {
            created_at
          }
        }
      }
      buyer_leads_aggregate {
        aggregate {
          count
        }
      }
      offers_aggregate {
        aggregate {
          count
        }
      }
      live_portals_count: portal_listings_aggregate(
        where: { live: { _eq: true } }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`);

type MatchingListing = GetListingsMatchingQuery['lots'][number];

const getMatchingListingsColumns = (
  t: Translate,
  locale: IntlLocale,
): GridColDef<MatchingListing>[] => [
  {
    field:
      'property.postcode,property.locality,property.route,property.street_number',
    headerName: t('Listing'),
    width: 250,
    display: 'flex',
    renderCell: ({ row }) => <ListingCell listing={row} />,
  },
  {
    field: 'sale_price, rent_net',
    headerName: t('Price'),
    width: 200,
    align: 'right',
    display: 'flex',
    renderCell: ({ row }) => {
      const prices = getListingPrices(row, t, locale);
      return (
        <Typography variant="body2">
          {prices.primary != null ? prices.primary : t('On request')}
        </Typography>
      );
    },
  },
  {
    field: 'broker.last_name,broker.first_name',
    headerName: t('Agent'),
    width: 200,
    renderCell: ({ row }) => row.broker && <UserInfo user={row.broker} />,
  },
  {
    field: 'status',
    headerName: t('Status'),
    width: 120,
    display: 'flex',
    renderCell: ({ row }) => (
      <Tag status={getLotStatusTagVariant(row.status)}>
        {row.lots_status.label ? row.lots_status.label : row.status}
      </Tag>
    ),
  },
  {
    field: 'live_portals_count.aggregate.count',
    headerName: t('Published'),
    width: 100,
    display: 'flex',
    renderCell: ({ row }) => {
      const livePortalsCount = row.live_portals_count.aggregate?.count ?? 0;

      return (
        <Tag status={livePortalsCount > 0 ? 'success' : 'neutral'}>
          {livePortalsCount > 0
            ? t('Online {{count}}', { count: livePortalsCount })
            : t('Offline')}
        </Tag>
      );
    },
  },
  {
    field: 'last_enquiry.aggregate.max.created_at',
    headerName: t('Last enquiry'),
    width: 120,
    display: 'flex',
    renderCell: ({ row }) =>
      row.last_enquiry.aggregate?.max?.created_at != null ? (
        <TimeAgo
          dateString={row.last_enquiry.aggregate?.max?.created_at}
          addSuffix
        />
      ) : (
        <Typography variant="body2">-</Typography>
      ),
  },
  {
    field: 'buyer_leads_aggregate.aggregate.count',
    headerName: t('Buyer leads'),
    width: 100,
    valueGetter: (_, row) => row.buyer_leads_aggregate.aggregate?.count ?? 0,
  },
  {
    field: 'offers_aggregate.aggregate.count',
    headerName: t('Offers'),
    width: 100,
    valueGetter: (_, row) => row.offers_aggregate.aggregate?.count ?? 0,
  },
];

const useQuickFilters = (): QuickFilterDefinition[] => {
  const { t } = useLocale();

  return useMemo(
    () => [
      {
        label: t('Created'),
        path: ['lots_bool_exp', 'created_at'],
      },
      {
        label: t('Buy / Rent'),
        path: ['lots_bool_exp', 'offer_type', '_in'],
      },
      {
        label: t('Sale price'),
        path: ['lots_bool_exp', 'sale_price'],
        disabled: (where: any) => {
          const value: string[] | undefined = getValueFromPath(
            ['lots_bool_exp', 'offer_type', '_in'],
            where,
          );

          return value == null || value.includes('rent');
        },
      },
      {
        label: t('Rent'),
        path: ['lots_bool_exp', 'rent_net'],
        disabled: (where: any) => {
          const value: string[] | undefined = getValueFromPath(
            ['lots_bool_exp', 'offer_type', '_in'],
            where,
          );

          return value == null || value.includes('buy');
        },
      },
      {
        label: t('Location'),
        path: ['lots_bool_exp', 'property', 'places'],
        includeParentsPlaces: true,
      },
      { label: t('Status'), path: ['lots_bool_exp', 'status', '_in'] },
      {
        label: t('Property type'),
        path: ['lots_bool_exp', 'property', 'property_type', 'name', '_in'],
        displayedColumn: 'label',
        filter: {
          path: ['property_type', 'type', '_neq'],
          value: 'main',
        },
        orderBy: [
          {
            composite_type: Order_By.AscNullsLast,
          },
          {
            type: Order_By.Asc,
          },
          {
            label: Order_By.Asc,
          },
        ] as Array<Record<string, Order_By>>,
      },
      {
        label: t('Rooms'),
        path: ['lots_bool_exp', 'property', 'number_of_rooms'],
      },
      {
        label: t('Bathrooms'),
        path: ['lots_bool_exp', 'property', 'number_of_bathrooms'],
      },
      {
        label: t('Living surface'),
        path: ['lots_bool_exp', 'property', 'living_surface'],
      },
      {
        label: t('Land surface'),
        path: ['lots_bool_exp', 'property', 'land_surface'],
      },
      {
        label: t('Source'),
        path: ['lots_bool_exp', 'origin', 'id', '_in'],
        displayedColumn: 'label',
        filter: {
          path: ['name', 'type', '_in'],
          value: 'lot_origin_types',
        },
      },
      {
        label: t('Online'),
        path: ['lots_bool_exp', 'portal_listings', 'live'],
      },
    ],
    [t],
  );
};

export const UserBuyerLeadMatchingList: React.FC<
  UserBuyerLeadMatchingListProps
> = ({ excludedListings, onAdd, isAdding, onCancel }) => {
  const { t, locale } = useLocale();

  const { where, order_by, limit, offset } = useListingsMatchingParams({
    id: {
      _nin: excludedListings,
    },
  });

  const { data, loading, error, refetch } = useQuery(GET_LISTINGS_MATCHING, {
    variables: {
      where,
      order_by,
      limit,
      offset,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: countData } = useQuery(GET_LOTS_LIST_COUNT, {
    variables: {
      where,
    },
  });

  // We use a set to prevent adding duplicates when selecting listings.
  const [selectedListingsIds, setSelectedListingIds] = useState(
    new Set<string>(),
  );
  const columns = useMemo(
    () => getMatchingListingsColumns(t, locale),
    [t, locale],
  );

  const rows: GridRowsProp<MatchingListing> = data?.lots ?? [];

  const quickFilters = useQuickFilters();

  if (error) {
    return <MutationErrorAlert error={error} />;
  }

  return (
    <Stack sx={{ height: '100%' }}>
      <ListToolbar
        tableName="lots"
        quickFilters={quickFilters}
        queryParamsScope="matching"
        defaultFilterTypeExpanded="quick"
        refetch={refetch}
        actions={
          <Stack
            spacing={1}
            direction="row"
            sx={{
              p: 1,
            }}
          >
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isAdding}
              disabled={selectedListingsIds.size === 0}
              loadingIndicator={
                <CircularProgress disableShrink color="inherit" size={16} />
              }
              onClick={() => onAdd(Array.from(selectedListingsIds))}
            >
              {t('Add listings')}
            </LoadingButton>
            <Button variant="outlined" onClick={onCancel}>
              {t('Cancel')}
            </Button>
          </Stack>
        }
      />
      <RaDataGrid
        loading={loading}
        columns={columns}
        queryParamsScope="matching"
        rows={rows}
        rowHeight={60}
        rowCount={countData?.lots_aggregate.aggregate?.count ?? 0}
        checkboxSelection
        approximateRowCount
        onRowSelectionModelChange={newSelection => {
          // Exclude-mode selections carry the *excluded* ids, not the included
          // ones — refuse them here so callers don't act on the wrong set.
          if (newSelection.type !== 'include') {
            setSelectedListingIds(new Set());
            return;
          }
          setSelectedListingIds(
            new Set(Array.from(newSelection.ids).map(id => id.toString())),
          );
        }}
        pageSizeOptions={[25, 50, 100, 500]}
      />
    </Stack>
  );
};
